import { generatePath, useNavigate, useParams } from 'react-router-dom'
import clsx from 'clsx'
import { paths } from 'src/router/paths'
import { ACCESS_TO_POST } from 'src/utils/constants'
import i18n from 'src/i18n'
import { NewsPostType } from 'src/hook/usePosts'
import { convertObjectToQueryString } from 'src/utils/functions'
import { useUser } from 'src/hook/useUser'
import { ThemeMode } from '@hotmart/space-lib/dist/styles/themes/enum'
import { useTrackDatahubEvents, ClickReadCommentsEventParams } from 'src/hook/useTrackDatahubEvents'
import { Action, Entity } from 'src/utils/datahubDefaultEvent'
import { ComponentNames } from 'src/utils/datahub/enums'
import Loading from '../Loading'
import { usePostFilters } from '../postFilters/hooks'
import { FeedCardProps, UrlPathParams } from './feedCard.types'
import { FeedCardBody, FeedCardFooter, FeedCardHeader, FeedCardLiveBar } from './components'
import { LoadingWrapper, Wrapper } from './feedCard.styles'

const FeedCard = ({
  post,
  highlight,
  enableCommentInput,
  isScheduledPost,
  isProcessingVideo,
  isProcessingAudio,
  className
}: FeedCardProps) => {
  const navigate = useNavigate()
  const { lang, membership } = useParams<UrlPathParams>()
  const { normalizeFilters } = usePostFilters({ updateQueryParams: false })
  const { theme } = useUser()
  const { trackClickEvent } = useTrackDatahubEvents()

  const isProcessingStatus = isProcessingVideo || isProcessingAudio
  const path = isScheduledPost ? paths.COMMUNITY_SCHEDULED_POST_PATH : paths.COMMUNITY_POST_PATH
  const postBasePath = generatePath(path, {
    lang,
    membership,
    idCommunity: String(post?.ownerCollectionId),
    idPost: String(post?.id)
  })

  const filtersQueryParams = convertObjectToQueryString(normalizeFilters())
  const queryParams = filtersQueryParams ? `&${filtersQueryParams}` : ''
  const postPath = `${postBasePath}?origin=${ACCESS_TO_POST.COMMUNITY_POST_FEED}&postId=${post.id}${queryParams}`
  const isLivePost = post.postType === NewsPostType.LIVE

  const handleClickComments = () => {
    trackClickEvent<ClickReadCommentsEventParams>({
      action: Action.CLICK_READ_COMMENTS,
      payload: {
        entity: Entity.COMMUNITY,
        componentName: ComponentNames.POST_FEED,
        hasAccessToTheCommunity: true,
        postId: post.id,
        commentCount: post?.commentsCount
      }
    })

    navigate(postPath)
  }

  return (
    <Wrapper
      className={clsx(className, '_rounded-lg _position-relative', {
        '_bg-gray-100': theme === ThemeMode.DARK,
        '_bg-white': theme === ThemeMode.LIGHT
      })}
      data-post-id={post.id}
      $highlight={highlight}
    >
      {isProcessingStatus && (
        <LoadingWrapper className="_position-absolute _d-flex _justify-content-center _align-items-center">
          <Loading
            text={i18n.t(
              isProcessingVideo
                ? 'input_publish.loading_video_progress'
                : 'input_publish.loading_audio_progress'
            )}
          />
        </LoadingWrapper>
      )}

      <FeedCardHeader post={post} isScheduledPost={isScheduledPost} />

      {isLivePost && <FeedCardLiveBar post={post} />}

      <FeedCardBody post={post} postPath={postPath} isProcessingStatus={isProcessingStatus} />

      <FeedCardFooter
        post={post}
        enableCommentSuggestion
        enableMentions
        enableCommentInput={enableCommentInput && !highlight && !isScheduledPost}
        onClickCommentCounter={handleClickComments}
      />
    </Wrapper>
  )
}

export default FeedCard
